import {
  DEFAULT_PRESET_ID,
  type ExploreDateSelectionFields,
} from "../lib/explore-date-presets";

export type DashboardMode = "my-brand" | "competitors";

export type { ExploreDateSelectionFields };

export interface CompetitorFilters extends ExploreDateSelectionFields {
  // My side
  myIndustry: string | null;
  myBrand: string | null;       // stub — no user-brands API yet
  industryProducts: string[];   // multi-select
  // Competitor side
  competitorsIndustry: string | null;
  mainCompetitor: string | null;
  benchmark: string[];          // multi-select competitor brand names
  platforms: string[];          // ad-platform multi-select (BQ rt.source)
  dateRange: string;
}

export const DEFAULT_COMPETITOR_FILTERS: CompetitorFilters = {
  myIndustry: null,
  myBrand: null,
  industryProducts: [],
  competitorsIndustry: null,
  mainCompetitor: null,
  benchmark: [],
  platforms: ["meta"],
  dateRange: "90d",
  startDate: "",
  endDate: "",
  activePresetId: DEFAULT_PRESET_ID,
};

/**
 * The legacy lookback tokens.
 *
 * No longer a UI option list — the date picker's presets replaced that. These
 * remain the `dateRange` query wire values and the URL schema's enum, so the
 * labels are unused and kept only to preserve the tuple shape.
 */
export const DATE_RANGE_OPTIONS = [
  { value: "7d", label: "Last 7 days" },
  { value: "30d", label: "Last 30 days" },
  { value: "90d", label: "Last 90 days" },
  { value: "12m", label: "Last 12 months" },
] as const;

export interface MyBrandFilters extends ExploreDateSelectionFields {
  platforms: string[] | null; // null = All (no filter), [] = none selected, [ids] = specific
  accounts: string[] | null; // null = All (no filter), [] = none selected, [ids] = specific
  products: string[] | null; // null = All (no filter), [] = none selected, [ids] = specific
  dateRange: string;
}

export const DEFAULT_MY_BRAND_FILTERS: MyBrandFilters = {
  platforms: null,
  accounts: null,
  products: null,
  dateRange: "90d",
  startDate: "",
  endDate: "",
  activePresetId: DEFAULT_PRESET_ID,
};

/**
 * Whether a competitor filter set has the fields the KPI request requires.
 * Mirrors the backend `hasRequiredFilters` so we never fire a request the
 * server would just reject as incomplete.
 */
export function isCompetitorFiltersComplete(f: CompetitorFilters): boolean {
  return Boolean(f.myIndustry && f.myBrand && f.competitorsIndustry && f.mainCompetitor);
}

/**
 * Whether a my-brand filter set is complete enough to request KPI data.
 * null = All (valid); [] = none selected (invalid).
 */
export function isMyBrandFiltersComplete(f: MyBrandFilters): boolean {
  return (
    (f.accounts === null || f.accounts.length > 0) &&
    (f.platforms === null || f.platforms.length > 0) &&
    (f.products === null || f.products.length > 0)
  );
}
